import * as React from 'react';
import { ChevronDown } from 'lucide-react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import {
  Select as SelectRoot,
  SelectTrigger as SelectTriggerBase,
  SelectValue,
  SelectIcon,
  SelectContent as SelectContentBase,
  SelectItem as SelectItemBase,
} from '@/hen-ui/shadcn/select';
import {
  NativeSelect,
  NativeSelectOption,
} from '@/hen-ui/shadcn/native-select';
import {
  isFilledSelectedState,
  isOneLineSelectVariant,
  isTwoLineSelectVariant,
  selectRoundedClass,
  selectTriggerVariants,
  type SelectedStateTheme,
  type SelectRounded,
  type SelectTriggerVariantProps,
} from './select.variants';
import { useIsomorphicLayoutEffect } from '@/hen-ui/hooks/clampTriggerContext';
import { cn } from '@/hen-ui/utils';
import {
  pickedFieldTextClasses,
  type FieldTextStyleProps,
} from '@/hen-ui/utils/field-text-style';

export type SelectWidth = 'auto' | 'max-content';

const SELECT_WIDTH_CLASS = {
  auto: 'w-auto',
  'max-content': 'w-max shrink-0',
} as const satisfies Record<SelectWidth, string>;

/** Unset keeps the field stretched. An unknown value does the same. */
const selectWidthClass = (width: string | undefined): string | undefined =>
  width === 'auto' || width === 'max-content'
    ? SELECT_WIDTH_CLASS[width]
    : undefined;

const selectFlexWidth = (width: string | undefined): 'full' | SelectWidth =>
  width === 'auto' || width === 'max-content' ? width : 'full';

export interface SelectProps
  extends SelectTriggerVariantProps,
    FieldTextStyleProps {
  /**
   * Error state - when true, displays error styling and sets aria-invalid
   */
  error?: boolean;
  /**
   * Error message text - when provided, sets aria-describedby and aria-invalid
   */
  errorMessage?: string;
  /**
   * Helper text below the select. Omitted when `errorMessage` is set (error takes precedence).
   */
  helperText?: string;
  /**
   * Uses the browser and operating system's native picker instead of the
   * custom Radix picker. Visual `variant` styling applies independently.
   */
  native?: boolean;
  /**
   * Label text. `two-line` stacks it above the answer inside the button.
   * `one-line` writes it inside the button with the answer beside it.
   * The custom picker renders it in the trigger. The native picker overlays
   * it so the browser still draws the chosen option.
   */
  label?: string;
  /**
   * Field-name weight for the `two-line` label. Defaults to `'bold'`.
   * `'regular'` renders a lighter label where a tenant's form calls for it.
   * `one-line` keeps the default field text.
   */
  fieldNameWeight?: 'bold' | 'regular';
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Selected value. An empty string is treated as unset so `defaultValue`
   * and visitor picks still apply.
   */
  value?: string;
  /**
   * Default value
   */
  defaultValue?: string;
  /**
   * Callback when value changes
   */
  onValueChange?: (value: string) => void;
  /**
   * Whether the select is disabled
   */
  disabled?: boolean;
  /**
   * Children should be SelectItem components
   */
  children?: React.ReactNode;
  /**
   * ID for the select trigger
   */
  id?: string;
  /**
   * Native form field name. The native picker applies it to the visible
   * control; the custom Radix picker uses it on its hidden form control.
   */
  name?: string;
  /**
   * Marks the field required for native form validation. Forwarded to either
   * the visible native picker or the custom Radix picker's hidden form control.
   */
  required?: boolean;
  /**
   * ARIA described by
   */
  'aria-describedby'?: string;
  /**
   * ARIA invalid
   */
  'aria-invalid'?: string;
  /**
   * Fill of the dropdown button once a value is chosen. `default` leaves
   * the button as it is. `primary` uses the brand fill. `secondary` is black.
   * The label is white on either fill. An empty or invalid button is unchanged.
   */
  selectedStateTheme?: SelectedStateTheme;
  /**
   * Corner radius of the dropdown button. Unset keeps the style's own
   * corners. A value replaces them, including the extra-large corners on
   * `elevated`.
   */
  rounded?: SelectRounded | '';
  /**
   * Width of the field. Leave unset to stretch across the container.
   * `auto` uses the browser width. `max-content` sizes the field to its
   * label and keeps a flex row from shrinking it.
   */
  width?: SelectWidth | '';
}

export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectItemBase> {
  children: React.ReactNode;
}

const SelectRenderModeContext = React.createContext<'custom' | 'native'>(
  'custom',
);

const getNativeOptionProps = (
  props: Record<string, unknown>,
): React.ComponentPropsWithoutRef<typeof NativeSelectOption> =>
  Object.fromEntries(
    Object.entries(props).filter(
      ([key]) =>
        key === 'id' ||
        key === 'title' ||
        key.startsWith('aria-') ||
        key.startsWith('data-'),
    ),
  ) as React.ComponentPropsWithoutRef<typeof NativeSelectOption>;

const getNativeOptionText = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child);
      }

      if (
        !React.isValidElement<{
          children?: React.ReactNode;
          text?: unknown;
        }>(child)
      ) {
        return '';
      }

      if (child.props.children !== undefined) {
        return getNativeOptionText(child.props.children);
      }

      return typeof child.props.text === 'string' ||
        typeof child.props.text === 'number'
        ? String(child.props.text)
        : '';
    })
    .join('');

const assignRef = <T,>(ref: React.ForwardedRef<T>, value: T | null): void => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
};

/**
 * SelectItem component for use within Select
 */
export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectItemBase> | HTMLOptionElement,
  SelectItemProps
>(({ className, children, value, disabled, ...props }, ref) => {
  const renderMode = React.useContext(SelectRenderModeContext);
  const optionText = getNativeOptionText(children);
  // Authoring tools hand us an option before its value is filled in. A native
  // `<option>` submits its own text in that case, so mirror it; Radix instead
  // throws on an empty value, so an option with no text either is dropped.
  const resolvedValue =
    typeof value === 'string' && value.trim() !== ''
      ? value
      : optionText.trim();

  if (resolvedValue === '') return null;

  if (renderMode === 'native') {
    const nativeOptionProps = getNativeOptionProps(props);

    return (
      <NativeSelectOption
        {...nativeOptionProps}
        ref={ref as React.ForwardedRef<HTMLOptionElement>}
        className={className}
        value={resolvedValue}
        disabled={disabled}
      >
        {optionText}
      </NativeSelectOption>
    );
  }

  return (
    <SelectItemBase
      ref={ref as React.ForwardedRef<React.ElementRef<typeof SelectItemBase>>}
      className={cn(
        'rounded-sm py-xxs px-sm text-b5',
        'focus:bg-neutral-100 focus:text-body',
        className,
      )}
      value={resolvedValue}
      disabled={disabled}
      {...props}
    >
      {children}
    </SelectItemBase>
  );
});
SelectItem.displayName = SelectItemBase.displayName;

interface NativeSelectFieldProps {
  forwardedRef: React.ForwardedRef<HTMLButtonElement | HTMLSelectElement>;
  id: string;
  value: string | undefined;
  defaultValue: string | undefined;
  onValueChange: ((value: string) => void) | undefined;
  variant: SelectTriggerVariantProps['variant'];
  isInvalid: boolean;
  isTwoLine: boolean;
  isOneLine: boolean;
  label: string | undefined;
  labelId: string | undefined;
  fieldNameWeight: 'bold' | 'regular';
  placeholder: string;
  disabled: boolean | undefined;
  name: string | undefined;
  required: boolean | undefined;
  ariaInvalid: 'true' | 'false' | undefined;
  describedBy: string | undefined;
  children: React.ReactNode;
  fieldHint: React.ReactNode;
  fieldTextStyle: FieldTextStyleProps;
  selectedStateTheme: SelectedStateTheme | undefined;
  rounded: SelectRounded | '' | undefined;
  width: SelectWidth | '' | undefined;
}

/**
 * Browser-native picker. The selection lives in React state rather than in
 * the DOM: when slotted options remount, an uncontrolled `<select>` snaps to
 * its first option, while a controlled one re-applies the selection.
 */
const NativeSelectField = ({
  forwardedRef,
  id,
  value,
  defaultValue,
  onValueChange,
  variant,
  isInvalid,
  isTwoLine,
  isOneLine,
  label,
  labelId,
  fieldNameWeight,
  placeholder,
  disabled,
  name,
  required,
  ariaInvalid,
  describedBy,
  children,
  fieldHint,
  fieldTextStyle,
  selectedStateTheme,
  rounded,
  width,
}: NativeSelectFieldProps) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');
  const [hasInteracted, setHasInteracted] = React.useState(false);
  const [, forceRender] = React.useReducer((count: number) => count + 1, 0);

  // A cleared controlled value falls back to `defaultValue`. A changed
  // `defaultValue` applies only until the visitor has picked something.
  const [previous, setPrevious] = React.useState({ value, defaultValue });
  if (previous.value !== value || previous.defaultValue !== defaultValue) {
    const clearedControlledValue =
      value === undefined && previous.value !== undefined;
    const defaultChangedBeforePick =
      value === undefined &&
      previous.defaultValue !== defaultValue &&
      !hasInteracted;
    if (clearedControlledValue || defaultChangedBeforePick) {
      setInternalValue(defaultValue ?? '');
      setHasInteracted(false);
    }
    setPrevious({ value, defaultValue });
  }

  const selected = value ?? internalValue;
  const fieldText = pickedFieldTextClasses(
    Boolean(selected),
    isInvalid,
    fieldTextStyle,
  );
  const filledSelected = isFilledSelectedState(
    selectedStateTheme,
    Boolean(selected),
    isInvalid,
  );
  const widthClass = selectWidthClass(width);
  const fieldNameClass = cn(
    'text-l2',
    fieldNameWeight === 'regular' ? 'font-normal' : 'font-bold',
    filledSelected ? 'text-white' : fieldText.label,
  );
  const labelMeasureRef = React.useRef<HTMLLabelElement>(null);
  const [labelMeasure, setLabelMeasure] = React.useState({
    width: 0,
    measured: false,
  });

  // The browser draws the chosen option, so the label width has to be
  // measured before the option text can start beside it. Until that
  // measurement lands, the option text stays transparent: the server
  // markup would otherwise start it 20px in and draw it through the
  // label. A resize covers a font swap or a later text-size change.
  useIsomorphicLayoutEffect(() => {
    if (!isOneLine) return;
    const labelElement = labelMeasureRef.current;
    if (!labelElement) return;

    const measure = () => {
      const width = labelElement.offsetWidth;
      setLabelMeasure((current) =>
        current.measured && current.width === width
          ? current
          : { width, measured: true },
      );
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(labelElement);
    return () => observer.disconnect();
  }, [isOneLine, label, fieldText.size, filledSelected, selected]);

  const selectRef = React.useRef<HTMLSelectElement | null>(null);
  const setSelectRef = React.useCallback(
    (element: HTMLSelectElement | null) => {
      selectRef.current = element;
      assignRef(forwardedRef as React.ForwardedRef<HTMLSelectElement>, element);
    },
    [forwardedRef],
  );

  const defaultValueRef = React.useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  // A form reset rewrites the DOM selection behind React. Return to the
  // default and re-render so the controlled value is applied again.
  React.useEffect(() => {
    const form = selectRef.current?.form;
    if (!form) return;

    let active = true;
    const handleReset = () => {
      queueMicrotask(() => {
        if (!active) return;
        setInternalValue(defaultValueRef.current ?? '');
        setHasInteracted(false);
        forceRender();
      });
    };

    form.addEventListener('reset', handleReset);
    return () => {
      active = false;
      form.removeEventListener('reset', handleReset);
    };
  }, []);

  return (
    <Flex direction='column' gap='xxs' width={selectFlexWidth(width)}>
      <div className={cn('relative', widthClass ?? 'w-full')}>
        {isTwoLine && label && (
          <label
            id={labelId}
            htmlFor={id}
            className={cn(
              'absolute pointer-events-none left-3 top-xxs',
              fieldNameClass,
            )}
          >
            {label}
          </label>
        )}
        {isOneLine && label && (
          <label
            ref={labelMeasureRef}
            id={labelId}
            htmlFor={id}
            className={cn(
              'absolute pointer-events-none left-3 top-1/2 -translate-y-1/2',
              'text-b5 font-normal',
              fieldText.size,
              filledSelected
                ? 'text-white'
                : isInvalid && selected
                  ? 'text-error-500'
                  : selected
                    ? 'text-body'
                    : (fieldText.emptyColor ?? 'text-subtext'),
            )}
          >
            {label}
          </label>
        )}
        <NativeSelect
          ref={setSelectRef}
          id={id}
          style={
            isOneLine
              ? {
                  paddingLeft: `${labelMeasure.width + 20}px`,
                  ...(label && !labelMeasure.measured
                    ? { color: 'transparent' }
                    : {}),
                }
              : undefined
          }
          className={cn(
            selectTriggerVariants({
              variant,
              error: isInvalid,
              hasValue: Boolean(selected),
              selectedStateTheme,
            }),
            fieldText.size,
            fieldText.emptyColor,
            'appearance-none pr-10',
            isTwoLine && 'pt-7 pb-1.5',
            selectRoundedClass(rounded),
            widthClass,
          )}
          value={selected}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setInternalValue(next);
            setHasInteracted(true);
            onValueChange?.(next);
          }}
          disabled={disabled}
          name={name}
          required={required}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
          aria-labelledby={isTwoLine || isOneLine ? labelId : undefined}
          data-hen-id={COMPONENT_IDS.Select}
        >
          <NativeSelectOption
            value=''
            disabled
            hidden={placeholder.trim() === ''}
          >
            {placeholder}
          </NativeSelectOption>
          <SelectRenderModeContext.Provider value='native'>
            {children}
          </SelectRenderModeContext.Provider>
        </NativeSelect>
        <ChevronDown
          aria-hidden='true'
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2',
            filledSelected ? 'text-white' : 'text-black',
          )}
        />
      </div>
      {fieldHint}
    </Flex>
  );
};

/**
 * Select component following HEN Design System standards.
 *
 * A standardized select dropdown field with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Select placeholder="Select an option">
 *   <SelectItem value="1">Option 1</SelectItem>
 *   <SelectItem value="2">Option 2</SelectItem>
 * </Select>
 *
 * @example
 * // With label and controlled state
 * <label htmlFor="country-select">Country</label>
 * <Select id="country-select" value={value} onValueChange={setValue}>
 *   <SelectItem value="us">United States</SelectItem>
 *   <SelectItem value="ca">Canada</SelectItem>
 * </Select>
 *
 * @example
 * // With helper text
 * <Select placeholder="Select option" helperText="You can change this later.">
 *   <SelectItem value="1">Option 1</SelectItem>
 * </Select>
 *
 * @example
 * // With error state
 * <Select
 *   placeholder="Select option"
 *   error
 *   errorMessage="This field is required"
 * >
 *   <SelectItem value="1">Option 1</SelectItem>
 * </Select>
 *
 * @example
 * // Two-line label variant — bold label sits above the value/placeholder
 * // in a single 56px box. The label is always visible (no floating).
 * <Select variant="two-line" label="Country" placeholder="Select a country">
 *   <SelectItem value="us">United States</SelectItem>
 *   <SelectItem value="ca">Canada</SelectItem>
 * </Select>
 *
 * @example
 * // Browser-native select — uses the operating system's native picker
 * <Select native placeholder="Select a credit rating">
 *   <SelectItem value="excellent">Excellent</SelectItem>
 *   <SelectItem value="good">Good</SelectItem>
 * </Select>
 *
 * @example
 * // Native picker with two-line stacked chrome
 * <Select native variant="two-line" label="Credit rating">
 *   <SelectItem value="excellent">Excellent</SelectItem>
 *   <SelectItem value="good">Good</SelectItem>
 * </Select>
 */
export const Select = React.forwardRef<
  HTMLButtonElement | HTMLSelectElement,
  SelectProps
>(
  (
    {
      variant,
      error,
      errorMessage,
      helperText,
      native = false,
      label,
      fieldNameWeight = 'bold',
      placeholder = 'Select...',
      value: valueProp,
      defaultValue,
      onValueChange,
      disabled,
      children,
      id,
      name,
      required,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      placeholderSize,
      valueSize,
      placeholderColor,
      labelColor,
      selectedStateTheme,
      rounded,
      width,
    },
    ref,
  ) => {
    // Authoring tools persist a cleared text field as `''`. Treating that as a
    // controlled empty value would pin the field to the placeholder and mask
    // `defaultValue`, so an empty string leaves the field uncontrolled.
    const value = valueProp === '' ? undefined : valueProp;

    // Generate unique id if not provided
    const selectId = React.useId();
    const finalId = id || selectId;
    const errorId = errorMessage ? `${finalId}-error` : undefined;
    const helperId =
      helperText && !errorMessage ? `${finalId}-helper` : undefined;
    const labelId = label ? `${finalId}-label` : undefined;

    // `isOpen` powers the custom picker's chevron rotation.
    const [isOpen, setIsOpen] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value || defaultValue),
    );

    // React ignores `defaultValue` once a control is mounted, so clearing a
    // controlled value or changing the default while uncontrolled would leave
    // the previous selection on screen. Remount the control in those cases.
    // A changed `defaultValue` applies only until the visitor has picked
    // something, so a late default cannot discard their pick.
    const [hasInteracted, setHasInteracted] = React.useState(false);
    const [resetState, setResetState] = React.useState({
      value,
      defaultValue,
      key: 0,
    });
    if (
      resetState.value !== value ||
      resetState.defaultValue !== defaultValue
    ) {
      const clearedControlledValue =
        value === undefined && resetState.value !== undefined;
      const defaultChangedBeforePick =
        value === undefined &&
        resetState.defaultValue !== defaultValue &&
        !hasInteracted;
      const shouldReset = clearedControlledValue || defaultChangedBeforePick;
      setResetState({
        value,
        defaultValue,
        key: shouldReset ? resetState.key + 1 : resetState.key,
      });
      if (shouldReset) {
        setHasValue(Boolean(defaultValue));
        setHasInteracted(false);
      }
    }
    // Update hasValue when controlled value changes
    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(Boolean(value));
      }
    }, [value]);

    // Combine aria-describedby values (error message replaces helper in the DOM and ids)
    const describedBy =
      [ariaDescribedBy, helperId, errorId].filter(Boolean).join(' ') ||
      undefined;

    // Set aria-invalid based on error prop or errorMessage
    const isInvalid = error || Boolean(errorMessage);
    const finalAriaInvalid: 'true' | 'false' | undefined =
      ariaInvalid !== undefined
        ? (ariaInvalid as 'true' | 'false')
        : isInvalid
          ? 'true'
          : undefined;

    const handleValueChange = (newValue: string) => {
      setHasValue(Boolean(newValue));
      setHasInteracted(true);
      onValueChange?.(newValue);
    };

    const isTwoLine = isTwoLineSelectVariant(variant) && Boolean(label);
    const isOneLine = isOneLineSelectVariant(variant) && Boolean(label);
    const fieldTextStyle = {
      placeholderSize,
      valueSize,
      placeholderColor,
      labelColor,
    };
    const fieldText = pickedFieldTextClasses(
      hasValue,
      isInvalid,
      fieldTextStyle,
    );
    const filledSelected = isFilledSelectedState(
      selectedStateTheme,
      hasValue,
      isInvalid,
    );
    const fieldNameClass = cn(
      'text-l2',
      fieldNameWeight === 'regular' ? 'font-normal' : 'font-bold',
      filledSelected ? 'text-white' : fieldText.label,
    );
    const answerClass = cn(
      'font-normal line-clamp-1',
      fieldText.size ?? 'text-sm leading-5',
      filledSelected
        ? 'text-white'
        : hasValue && !isInvalid
          ? 'text-body'
          : (fieldText.emptyColor ?? 'text-subtext'),
      isInvalid && hasValue ? 'text-error-500' : '',
    );

    const fieldHint = helperId ? (
      <Text tag='div' id={helperId} variant='b5' color='subtext'>
        {helperText}
      </Text>
    ) : errorMessage ? (
      <Text
        tag='div'
        id={errorId}
        variant='b5'
        color='error'
        role='alert'
        aria-live='polite'
      >
        {errorMessage}
      </Text>
    ) : null;

    if (native) {
      return (
        <NativeSelectField
          forwardedRef={ref}
          id={finalId}
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          variant={variant}
          isInvalid={isInvalid}
          isTwoLine={isTwoLine}
          isOneLine={isOneLine}
          label={label}
          labelId={labelId}
          fieldNameWeight={fieldNameWeight}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          required={required}
          ariaInvalid={finalAriaInvalid}
          describedBy={describedBy}
          fieldHint={fieldHint}
          fieldTextStyle={fieldTextStyle}
          selectedStateTheme={selectedStateTheme}
          rounded={rounded}
          width={width}
        >
          {children}
        </NativeSelectField>
      );
    }

    return (
      <Flex direction='column' gap='xxs' width={selectFlexWidth(width)}>
        <div className={cn('relative', selectWidthClass(width) ?? 'w-full')}>
          <SelectRoot
            key={resetState.key}
            value={value}
            defaultValue={defaultValue}
            onValueChange={handleValueChange}
            disabled={disabled}
            name={name}
            required={required}
            onOpenChange={setIsOpen}
          >
            <SelectTriggerBase
              ref={ref as React.ForwardedRef<HTMLButtonElement>}
              id={finalId}
              className={cn(
                selectTriggerVariants({
                  variant,
                  error: isInvalid,
                  hasValue,
                  selectedStateTheme,
                }),
                !isTwoLine && fieldText.size,
                !isTwoLine && fieldText.emptyColor,
                selectRoundedClass(rounded),
                selectWidthClass(width),
              )}
              aria-invalid={finalAriaInvalid}
              aria-describedby={describedBy}
              aria-labelledby={isTwoLine || isOneLine ? labelId : undefined}
              data-hen-id={COMPONENT_IDS.Select}
            >
              {isTwoLine ? (
                // Stacked layout: bold label on top, SelectValue
                // (selected text or placeholder) underneath in
                // subtext color. The wrapping `<div>` is the flex
                // column the design spec calls for; the trigger
                // itself contributes the outer h-14 box, padding,
                // and chevron gap via `selectTriggerVariants`.
                <div className='flex flex-1 min-w-0 flex-col items-start text-left'>
                  <span id={labelId} className={cn('w-full', fieldNameClass)}>
                    {label}
                  </span>
                  <span className={cn('w-full', answerClass)}>
                    <SelectValue placeholder={placeholder} />
                  </span>
                </div>
              ) : isOneLine ? (
                // A div, not a span: the shared trigger class clamps every
                // direct child span, which would pull the answer onto the label.
                <div className='flex min-w-0 flex-1 items-center gap-2 text-left'>
                  <span id={labelId} className='shrink-0'>
                    {label}
                  </span>
                  <span className='min-w-0 truncate'>
                    <SelectValue placeholder={placeholder} />
                  </span>
                </div>
              ) : (
                <SelectValue placeholder={placeholder} />
              )}
              <SelectIcon asChild>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0',
                    filledSelected ? 'text-white' : 'text-black',
                    'transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </SelectIcon>
            </SelectTriggerBase>
            <SelectContentBase
              className={cn(
                'min-w-[8rem]',
                'rounded-sm border border-neutral-200 bg-white text-body shadow-md',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              )}
              viewportClassName='p-1'
              position='popper'
              sideOffset={4}
            >
              {placeholder.trim() !== '' && (
                <i className='py-xxs px-sm mb-xxs text-b5 text-body'>
                  {placeholder}
                </i>
              )}
              {children}
            </SelectContentBase>
          </SelectRoot>
        </div>
        {fieldHint}
      </Flex>
    );
  },
);

Select.displayName = 'Select';
